import { gql, useMutation } from '@apollo/client'

import {
  DeleteDocumentMutationMutation,
  DeleteDocumentMutationMutationVariables,
  UseDeleteDocument_DocumentFragment,
} from './__generated__/use-delete-document.graphql'

export type UseDeleteDocumentProps = {
  artifact: string
  document: UseDeleteDocument_DocumentFragment
  docId: number
}

export type UseDeleteDocumentReturn = {
  loading: boolean
  deleteDocument: () => Promise<void>
}

export function useDeleteDocument({
  artifact,
  document,
  docId,
}: UseDeleteDocumentProps): UseDeleteDocumentReturn {
  const [mutate, { loading }] = useMutation<
    DeleteDocumentMutationMutation,
    DeleteDocumentMutationMutationVariables
  >(DeleteDocumentMutation, {
    update(cache, { data }) {
      const { parentDocument } = data?.deleteDocument?.document || {}
      if (parentDocument) {
        cache.modify({
          id: cache.identify(document),
          fields: {
            numComments() {
              return parentDocument?.numComments
            },
          },
        })

        cache.evict({
          id: `DocumentComment:${docId}`,
        })
        cache.gc()
      }
    },
  })

  return {
    async deleteDocument() {
      const input = { docId, artifact }
      await mutate({ variables: { input } })
    },
    loading,
  }
}

useDeleteDocument.fragments = {
  document: gql`
    fragment UseDeleteDocument_document on Document {
      docId
    }
  `,
}

export const DeleteDocumentMutation = gql`
  mutation deleteDocumentMutation($input: DeleteDocumentInput!) {
    deleteDocument(input: $input) {
      document {
        docId
        parentDocument {
          id
          numComments
        }
      }
    }
  }
`
